import { useState } from 'react';
import type { CountryCode, CustomerAddonGroup, CustomerDish } from '@capsico/contracts';
import { track } from '../api/track';
import { cartStore, quantityOf, useCart, type NewLine } from './useCart';
import { showToast } from './useToast';

export interface CustomisedChoice {
  variantId: string | null;
  addonOptionIds: string[];
  comboPicks: { slotId: string; itemIds: string[] }[];
  quantity: number;
}

/** Builds a cart line from a dish and the customer's choices (names kept for display). */
export function lineFor(
  dish: CustomerDish,
  addonGroups: CustomerAddonGroup[],
  choice: CustomisedChoice,
): NewLine {
  const variant = choice.variantId
    ? dish.variants.find((entry) => entry.id === choice.variantId)
    : undefined;
  const options = addonGroups.flatMap((group) => group.options);
  const picked = choice.addonOptionIds
    .map((id) => options.find((option) => option.id === id))
    .filter((option): option is NonNullable<typeof option> => option !== undefined);
  const base = variant ? variant.priceMinor : dish.priceMinor;
  return {
    itemId: dish.id,
    name: dish.name,
    foodType: dish.foodType,
    variantId: variant?.id ?? null,
    variantName: dish.variants.length > 1 ? (variant?.name ?? null) : null,
    addonOptionIds: picked.map((option) => option.id),
    addonNames: picked.map((option) => option.name),
    comboPicks: choice.comboPicks.filter((pick) => pick.itemIds.length > 0),
    unitPriceMinor: base + picked.reduce((sum, option) => sum + option.priceMinor, 0),
    quantity: choice.quantity,
    note: null,
  };
}

/** W19 add_to_cart: the line value in minor units (no item names leave the phone). */
function trackAdd(outlet: { id: string; country: CountryCode }, line: NewLine) {
  track('add_to_cart', {
    country: outlet.country,
    outletId: outlet.id,
    valueMinor: line.unitPriceMinor * line.quantity,
    props: { quantity: line.quantity },
  });
}

/**
 * ADD on a restaurant page: plain dishes go straight in, customisable ones through the sheet.
 * A cart from another restaurant asks first ("Start a new cart?", pattern U).
 */
export function useAddToCart(
  outlet: { id: string; name: string; country: CountryCode } | null,
  messages: { added: string; full: string },
) {
  const cart = useCart();
  const [pending, setPending] = useState<NewLine | null>(null);

  const add = (line: NewLine) => {
    if (!outlet) return;
    const result = cartStore.add(outlet, line);
    if (result === 'added') trackAdd(outlet, line);
    if (result === 'other_restaurant') setPending(line);
    else if (result === 'full') showToast(messages.full, { tone: 'negative' });
  };

  return {
    cart,
    quantity: (itemId: string) => quantityOf(cart, itemId),
    add,
    removeOne: (itemId: string) => cartStore.removeOneOfItem(itemId),
    /** The cart belongs to another restaurant and a dish is waiting to replace it. */
    pending,
    otherRestaurant: cart && outlet && cart.outletId !== outlet.id ? cart.outletName : null,
    confirmReplace: () => {
      if (outlet && pending) {
        cartStore.replace(outlet, pending);
        trackAdd(outlet, pending);
        showToast(messages.added);
      }
      setPending(null);
    },
    cancelReplace: () => setPending(null),
  };
}
